// Occasion order-by urgency. An entry renders a deadline line in the
// customiser between showFrom and orderBy (Melbourne dates, inclusive),
// then self-expires - no deploy needed to turn it off. Add next year's
// occasions here as they approach.

export type Occasion = {
  slug: string;
  /** e.g. "Father's Day (Sun 6 Sept)" */
  eventLabel: string;
  /** Customer-facing deadline, e.g. "Fri 21 Aug, 12pm". */
  orderByLabel: string;
  orderByIso: string;
  showFromIso: string;
  /** Optional extra line, e.g. a shipping promo window. */
  note?: string;
};

export const OCCASIONS: Occasion[] = [
  {
    slug: "fathers-day-2026",
    // 2026-07-21: standard made-to-order closes Fri 21 Aug 12:00 AEST, rush
    // keeps selling to Sun 23 Aug. The old "Sun 23 Aug" label described the
    // rush deadline while promising a standard turnaround, and the note
    // advertised a free-express promo that was dropped and whose discount is
    // now deactivated. orderByIso stays date-only (this gate has no clock), so
    // the banner retires at the end of 21 Aug rather than at midday.
    eventLabel: "Father's Day (Sun 6 Sept)",
    orderByLabel: "Fri 21 Aug, 12pm",
    orderByIso: "2026-08-21",
    showFromIso: "2026-07-19",
    note: "Need it faster? Rush orders stay open until Sun 23 Aug",
  },
];

export function melbTodayIso(): string {
  try {
    const parts = new Intl.DateTimeFormat("en-AU", {
      timeZone: "Australia/Melbourne",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date());
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
    return `${get("year")}-${get("month")}-${get("day")}`;
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

/** The occasion whose window covers today, with whole days remaining
 *  until the order-by deadline. Null when nothing is on. */
export function activeOccasion(
  todayIso: string = melbTodayIso(),
): (Occasion & { daysLeft: number }) | null {
  for (const o of OCCASIONS) {
    if (todayIso < o.showFromIso || todayIso > o.orderByIso) continue;
    const daysLeft = Math.max(
      0,
      Math.round((Date.parse(o.orderByIso) - Date.parse(todayIso)) / 86400000),
    );
    return { ...o, daysLeft };
  }
  return null;
}
